import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  usersApi,
  distributionApi,
  globalSettingsApi,
  meetingPromptsApi,
  InstallerItem,
  GlobalSettings,
  MeetingPromptsDto,
} from '../services/api';

const MEETING_PROMPT_MIN_LENGTH = 80;
const MEETING_PROMPT_MAX_LENGTH = 500;

const TRANSCRIPTION_MODEL_LABELS: Record<string, string> = {
  'nova-2': 'Nova 2',
  'nova-2-meeting': 'Nova 2 Meeting',
  'nova-3': 'Nova 3',
  'nova-3-meeting': 'Nova 3 Meeting',
};

const UserProfile: React.FC = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState<string>(user?.name || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [installers, setInstallers] = useState<InstallerItem[]>([]);
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [dragActiveSlot, setDragActiveSlot] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadSlot = useRef<{ platform: string; installerType: string } | null>(null);
  const isAdmin = user?.role === 'ADMIN';

  const INSTALLER_SLOTS = [
    { platform: 'windows', installerType: 'msi', label: 'Windows (MSI)', accept: '.msi' },
    { platform: 'windows', installerType: 'exe', label: 'Windows (EXE)', accept: '.exe' },
    { platform: 'mac', installerType: 'installer', label: 'macOS', accept: '.dmg,.app' },
    { platform: 'linux', installerType: 'installer', label: 'Linux', accept: '.deb,.rpm' },
  ] as const;

  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);
  const [transcriptionModel, setTranscriptionModel] = useState<string>('nova-2');
  const [transcriptionLanguage, setTranscriptionLanguage] = useState<string>('auto');
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [saveGlobalSuccess, setSaveGlobalSuccess] = useState(false);

  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<'USER' | 'ADMIN'>('USER');
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserMessage, setCreateUserMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [meetingPrompts, setMeetingPrompts] = useState<MeetingPromptsDto | null>(null);
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
  const [editingTemplateTitle, setEditingTemplateTitle] = useState('');
  const [editingTemplatePrompt, setEditingTemplatePrompt] = useState('');
  const [savingTemplates, setSavingTemplates] = useState(false);
  const [templatesMessage, setTemplatesMessage] = useState<string | null>(null);

  useEffect(() => {
    if (user?.name) {
      setName(user.name);
    }
  }, [user?.name]);

  useEffect(() => {
    if (isAdmin) {
      loadInstallers();
      loadGlobalSettings();
    }
  }, [isAdmin]);

  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const data = await meetingPromptsApi.getMyPrompts();
        setMeetingPrompts(data);
        setSelectedTemplateIndex(
          data.currentTemplateIndex >= 0 &&
            data.currentTemplateIndex < data.templates.length
            ? data.currentTemplateIndex
            : 0,
        );
        const current = data.templates[
          data.currentTemplateIndex >= 0 &&
          data.currentTemplateIndex < data.templates.length
            ? data.currentTemplateIndex
            : 0
        ];
        setEditingTemplateTitle(current?.title ?? '');
        setEditingTemplatePrompt(current?.prompt ?? '');
      } catch (error) {
        console.error('Failed to load meeting prompts:', error);
        setMeetingPrompts(null);
      }
    };
    if (user) {
      loadPrompts();
    }
  }, [user]);

  const loadInstallers = async () => {
    try {
      const list = await distributionApi.getAllInstallers();
      setInstallers(list);
    } catch (error) {
      console.error('Failed to load installers:', error);
      setInstallers([]);
    }
  };

  const loadGlobalSettings = async () => {
    try {
      const settings = await globalSettingsApi.getGlobalSettings();
      setGlobalSettings(settings);
      const model = settings.transcriptionModelDeepgram ?? 'nova-2';
      setTranscriptionModel(model);
      const lang = settings.transcriptionLanguageDeepgram ?? 'auto';
      const supported = settings.supportedLanguagesByModel?.[model] ?? [];
      const validLang = lang === 'auto' || supported.some((l) => l.code === lang) ? lang : 'auto';
      setTranscriptionLanguage(validLang);
    } catch (error) {
      console.error('Failed to load global settings:', error);
      setGlobalSettings(null);
    }
  };

  const currentLanguageOptions = (globalSettings?.supportedLanguagesByModel?.[transcriptionModel] ?? []);
  const languageValue = transcriptionLanguage === 'auto'
    ? 'auto'
    : (currentLanguageOptions.some((o) => o.code === transcriptionLanguage) ? transcriptionLanguage : 'auto');

  const handleTranscriptionModelChange = (newModel: string) => {
    setTranscriptionModel(newModel);
    const langs = globalSettings?.supportedLanguagesByModel?.[newModel] ?? [];
    const currentSupported = langs.some((l) => l.code === transcriptionLanguage);
    if (!currentSupported) {
      setTranscriptionLanguage('auto');
    }
  };

  const handleSaveGlobalSettings = async () => {
    if (!globalSettings) return;
    try {
      setSavingGlobal(true);
      const updated = await globalSettingsApi.updateGlobalSettings({
        transcriptionModelDeepgram: transcriptionModel,
        transcriptionLanguageDeepgram: languageValue,
      });
      setGlobalSettings(updated);
      setSaveGlobalSuccess(true);
      setTimeout(() => setSaveGlobalSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to update global settings:', error);
      alert(error.response?.data?.message || 'Failed to update global settings. Please try again.');
    } finally {
      setSavingGlobal(false);
    }
  };

  const getInstallerForSlot = (platform: string, installerType: string) =>
    installers.find((i) => i.platform === platform && i.installerType === installerType);

  const handleUploadForSlot = (platform: string, installerType: string) => {
    pendingUploadSlot.current = { platform, installerType };
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (file: File) => {
    const slot = pendingUploadSlot.current;
    if (!file || !slot) return;

    const slotKey = `${slot.platform}/${slot.installerType}`;
    try {
      setUploadingSlot(slotKey);
      await distributionApi.uploadDesktopApp(file, slot.platform, slot.installerType);
      await loadInstallers();
      alert('Installer uploaded successfully!');
    } catch (error: any) {
      console.error('Failed to upload installer:', error);
      alert(error.response?.data?.message || 'Failed to upload installer. Please try again.');
    } finally {
      setUploadingSlot(null);
      pendingUploadSlot.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDrag = (slotKey: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActiveSlot(slotKey);
    else if (e.type === 'dragleave') setDragActiveSlot(null);
  };

  const handleDrop = (platform: string, installerType: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveSlot(null);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      pendingUploadSlot.current = { platform, installerType };
      handleFileSelect(file);
    }
  };

  const handleDeleteInstaller = async (id: string) => {
    if (!window.confirm('Delete this installer? Users will no longer be able to download it.')) return;
    try {
      setDeletingId(id);
      await distributionApi.deleteDesktopApp(id);
      await loadInstallers();
      alert('Installer deleted.');
    } catch (error: any) {
      console.error('Failed to delete installer:', error);
      alert(error.response?.data?.message || 'Failed to delete installer.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSaveName = async () => {
    if (!isEditingName) return;

    try {
      setSaving(true);
      const updated = await usersApi.updateUserProfile(name);
      // Update user in auth context
      setUser({ ...user!, name: updated.name });
      setIsEditingName(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to update name:', error);
      alert(error.response?.data?.message || 'Failed to update name. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset to original name
    setName(user?.name || '');
    setIsEditingName(false);
  };

  const handleCreateUser = async () => {
    const email = newUserEmail.trim();
    const password = newUserPassword;
    if (!email || !password) {
      setCreateUserMessage({ type: 'error', text: 'Email and password are required.' });
      return;
    }
    if (password.length < 8) {
      setCreateUserMessage({ type: 'error', text: 'Password must be at least 8 characters long.' });
      return;
    }
    try {
      setCreatingUser(true);
      setCreateUserMessage(null);
      await usersApi.createUser({
        email,
        password,
        name: newUserName.trim() || undefined,
        role: newUserRole,
      });
      setCreateUserMessage({ type: 'success', text: 'User created successfully.' });
      setNewUserEmail('');
      setNewUserName('');
      setNewUserPassword('');
      setNewUserRole('USER');
      setTimeout(() => setCreateUserMessage(null), 5000);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to create user. Please try again.';
      setCreateUserMessage({ type: 'error', text: msg });
    } finally {
      setCreatingUser(false);
    }
  };

  const trimmedPrompt = editingTemplatePrompt.trim();
  const promptLength = trimmedPrompt.length;
  const isPromptTooShort = promptLength > 0 && promptLength < MEETING_PROMPT_MIN_LENGTH;
  const isPromptTooLong = promptLength > MEETING_PROMPT_MAX_LENGTH;

  const canSaveTemplate =
    editingTemplateTitle.trim().length > 0 &&
    promptLength >= MEETING_PROMPT_MIN_LENGTH &&
    promptLength <= MEETING_PROMPT_MAX_LENGTH;

  const handleSelectTemplate = (index: number) => {
    if (!meetingPrompts) return;
    setSelectedTemplateIndex(index);
    const tpl = meetingPrompts.templates[index];
    setEditingTemplateTitle(tpl.title);
    setEditingTemplatePrompt(tpl.prompt);
  };

  const handleSaveTemplate = async () => {
    if (!meetingPrompts || !canSaveTemplate) return;
    try {
      setSavingTemplates(true);
      setTemplatesMessage(null);
      const templates = [...meetingPrompts.templates];
      templates[selectedTemplateIndex] = {
        title: editingTemplateTitle.trim(),
        prompt: editingTemplatePrompt.trim(),
      };
      const payload: MeetingPromptsDto = {
        templates,
        currentTemplateIndex: selectedTemplateIndex,
      };
      const updated = await meetingPromptsApi.updateMyPrompts(payload);
      setMeetingPrompts(updated);
      setTemplatesMessage('Template saved.');
      setTimeout(() => setTemplatesMessage(null), 3000);
    } catch (error: any) {
      console.error('Failed to save meeting template:', error);
      alert(
        error.response?.data?.message ||
          'Failed to save meeting template. Please check title and prompt lengths.',
      );
    } finally {
      setSavingTemplates(false);
    }
  };

  const handleAddTemplate = async () => {
    if (!meetingPrompts) return;
    const templates = [
      ...meetingPrompts.templates,
      {
        title: 'New template',
        prompt: '',
      },
    ];
    const index = templates.length - 1;
    setMeetingPrompts({
      templates,
      currentTemplateIndex: index,
    });
    setSelectedTemplateIndex(index);
    setEditingTemplateTitle('New template');
    setEditingTemplatePrompt('');
    setTemplatesMessage('New template added. Fill in the fields and click "Save template".');
    setTimeout(() => setTemplatesMessage(null), 4000);
  };

  const handleDeleteTemplate = async () => {
    if (!meetingPrompts) return;
    if (meetingPrompts.templates.length <= 1) {
      alert('At least one template must exist.');
      return;
    }
    const templates = meetingPrompts.templates.filter(
      (_tpl, idx) => idx !== selectedTemplateIndex,
    );
    const newIndex =
      selectedTemplateIndex >= templates.length
        ? templates.length - 1
        : selectedTemplateIndex;
    const updatedLocal: MeetingPromptsDto = {
      templates,
      currentTemplateIndex: newIndex,
    };
    setMeetingPrompts(updatedLocal);
    setSelectedTemplateIndex(newIndex);
    const tpl = updatedLocal.templates[newIndex];
    setEditingTemplateTitle(tpl.title);
    setEditingTemplatePrompt(tpl.prompt);
    setTemplatesMessage('Template deleted locally. Click "Save template" to persist changes.');
    setTimeout(() => setTemplatesMessage(null), 4000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            User Profile
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Profile Details Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Profile Details</h2>

          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              {isEditingName ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Enter your name"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName();
                      } else if (e.key === 'Escape') {
                        handleCancelEdit();
                      }
                    }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={saving}
                    className="px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={saving}
                    className="px-4 py-3 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <p className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800">
                    {name || <span className="text-gray-400 italic">No name set</span>}
                  </p>
                  <button
                    onClick={() => setIsEditingName(true)}
                    className="px-4 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-all shadow-md hover:shadow-lg"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <p className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-800">
                {user?.email || 'N/A'}
              </p>
              <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
            </div>

            {saveSuccess && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">✅ Profile updated successfully!</p>
              </div>
            )}
          </div>
        </div>

        {/* Meeting Settings Section */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Meeting Settings</h2>
          <p className="text-sm text-gray-600 mb-4">
            Configure AI meeting summary templates used for your transcripts.
          </p>
          {meetingPrompts ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium text-gray-700">
                  Summary template
                </label>
                <select
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                  value={selectedTemplateIndex}
                  onChange={(e) => handleSelectTemplate(Number(e.target.value))}
                  disabled={savingTemplates}
                >
                  {meetingPrompts.templates.map((tpl, idx) => (
                    <option key={idx} value={idx}>
                      {tpl.title}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleAddTemplate}
                  disabled={savingTemplates}
                  className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add template
                </button>
                <button
                  type="button"
                  onClick={handleDeleteTemplate}
                  disabled={savingTemplates || (meetingPrompts.templates?.length ?? 0) <= 1}
                  className="px-3 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Delete selected
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template title
                </label>
                <input
                  type="text"
                  value={editingTemplateTitle}
                  onChange={(e) => setEditingTemplateTitle(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="e.g. Real estate client meeting summary"
                  disabled={savingTemplates}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template prompt
                </label>
                <textarea
                  value={editingTemplatePrompt}
                  onChange={(e) => setEditingTemplatePrompt(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder={`Describe how the AI should summarize your meetings (${MEETING_PROMPT_MIN_LENGTH}–${MEETING_PROMPT_MAX_LENGTH} characters).`}
                  disabled={savingTemplates}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Between {MEETING_PROMPT_MIN_LENGTH} and {MEETING_PROMPT_MAX_LENGTH} characters. This template
                  is used for both partial and final summaries.
                </p>
                {isPromptTooShort && (
                  <p className="text-xs text-red-600 mt-1">
                    Prompt is too short ({promptLength} characters). Minimum is {MEETING_PROMPT_MIN_LENGTH}.
                  </p>
                )}
                {isPromptTooLong && (
                  <p className="text-xs text-red-600 mt-1">
                    Prompt is too long ({promptLength} characters). Maximum is {MEETING_PROMPT_MAX_LENGTH}.
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveTemplate}
                  disabled={!canSaveTemplate || savingTemplates}
                  className="px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                >
                  {savingTemplates ? 'Saving template...' : 'Save template'}
                </button>
                {templatesMessage && (
                  <span className="text-sm text-green-700">{templatesMessage}</span>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Loading meeting settings…</p>
          )}
        </div>

        {/* Global Settings Section (Admin Only) */}
        {isAdmin && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Global Settings</h2>
            <p className="text-sm text-gray-600 mb-4">
              Meeting transcription model and language. These apply to all users.
            </p>
            {globalSettings && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transcription model (Deepgram)
                  </label>
                  <select
                    value={transcriptionModel}
                    onChange={(e) => handleTranscriptionModelChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  >
                    {(globalSettings.transcriptionModels ?? ['nova-2', 'nova-2-meeting', 'nova-3', 'nova-3-meeting']).map((m) => (
                      <option key={m} value={m}>
                        {TRANSCRIPTION_MODEL_LABELS[m] ?? m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transcription language
                  </label>
                  <select
                    value={languageValue}
                    onChange={(e) => setTranscriptionLanguage(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  >
                    <option value="auto">Auto</option>
                    {currentLanguageOptions.map((opt) => (
                      <option key={opt.code} value={opt.code}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Auto detects the spoken language. Available languages depend on the selected model.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSaveGlobalSettings}
                    disabled={savingGlobal}
                    className="px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                  >
                    {savingGlobal ? 'Saving...' : 'Save global settings'}
                  </button>
                  {saveGlobalSuccess && (
                    <span className="text-sm text-green-700">Saved.</span>
                  )}
                </div>
              </div>
            )}
            {!globalSettings && isAdmin && (
              <p className="text-sm text-gray-500">Loading global settings...</p>
            )}
          </div>
        )}

        {/* User Management Section (Admin Only) */}
        {isAdmin && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">User Management</h2>
            <p className="text-sm text-gray-600 mb-4">
              Create new user accounts. The new user can sign in with the email and password you set.
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="new-user-email">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="new-user-email"
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="new-user-name">
                    Name
                  </label>
                  <input
                    id="new-user-name"
                    type="text"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    placeholder="Display name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="new-user-role">
                    Role
                  </label>
                  <select
                    id="new-user-role"
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value as 'USER' | 'ADMIN')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  >
                    <option value="USER">USER</option>
                    <option value="ADMIN">ADMIN</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="new-user-password">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="new-user-password"
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent max-w-md"
                  autoComplete="new-password"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>
              {createUserMessage && (
                <div
                  className={`p-3 rounded-lg text-sm ${
                    createUserMessage.type === 'success'
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                >
                  {createUserMessage.type === 'success' ? '✅ ' : '❌ '}
                  {createUserMessage.text}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={handleCreateUser}
                  disabled={creatingUser}
                  className="px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
                >
                  {creatingUser ? 'Creating...' : 'Create user'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Distribution Section (Admin Only) */}
        {isAdmin && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">Distribution</h2>
            <p className="text-sm text-gray-600 mb-4">
              Upload installers per platform. Users see a download button for each installer you provide.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileInputChange}
              accept=".app,.dmg,.msi,.exe,.deb,.rpm"
              className="hidden"
            />
            <div className="grid gap-4 sm:grid-cols-2">
              {INSTALLER_SLOTS.map(({ platform, installerType, label, accept }) => {
                const current = getInstallerForSlot(platform, installerType);
                const slotKey = `${platform}/${installerType}`;
                const isUploading = uploadingSlot === slotKey;
                const isDeleting = current && deletingId === current.id;
                return (
                  <div
                    key={slotKey}
                    className="border border-gray-200 rounded-lg p-4"
                    onDragEnter={handleDrag(slotKey)}
                    onDragLeave={handleDrag(slotKey)}
                    onDragOver={handleDrag(slotKey)}
                    onDrop={handleDrop(platform, installerType)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800">{label}</span>
                      {current && (
                        <button
                          onClick={() => handleDeleteInstaller(current.id)}
                          disabled={isDeleting}
                          className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                        >
                          {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                    {current ? (
                      <>
                        <p className="text-sm text-gray-600 truncate" title={current.fileName}>
                          {current.fileName}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Uploaded: {new Date(current.updatedAt).toLocaleString()}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">No file uploaded</p>
                    )}
                    <div
                      className={`mt-2 border-2 border-dashed rounded p-4 text-center transition-colors ${
                        dragActiveSlot === slotKey ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 bg-gray-50'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleUploadForSlot(platform, installerType)}
                        disabled={isUploading}
                        className="text-sm text-indigo-600 hover:text-indigo-700 font-medium disabled:opacity-50"
                      >
                        {isUploading ? 'Uploading...' : current ? 'Replace file' : 'Browse or drop file'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
